/**
 * Cart Progress Bar Component
 * Updates the progress bar when cart changes
 */

class CartProgressBar extends HTMLElement {
  constructor() {
    super();
    this.cartUpdateUnsubscriber = undefined;
    this.freeShippingThreshold = 5000; // $50.00 in cents
    this.surpriseGiftThreshold = 8000; // $80.00 in cents
  }

  connectedCallback() {
    // Subscribe to cart update events
    if (typeof subscribe !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
      this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        if (event.cartData) {
          this.updateProgressBar(event.cartData);
        }
      });
    }

    // Initial update with current cart data
    this.updateFromCart();

    // Watch for cart drawer content replacement
    this.observeCartDrawer();
  }

  observeCartDrawer() {
    // If inside a cart drawer, watch for content replacement
    const cartDrawer = this.closest('cart-drawer');
    if (cartDrawer) {
      // Watch for cart drawer items replacement
      const cartDrawerItems = document.querySelector('cart-drawer-items');
      if (cartDrawerItems) {
        const observer = new MutationObserver(() => {
          // When cart drawer items are replaced, update progress bar
          setTimeout(() => this.updateFromCart(), 200);
        });

        observer.observe(cartDrawer, { childList: true, subtree: true });
        
        // Store observer for cleanup
        this.drawerObserver = observer;
      }
    }
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
    if (this.drawerObserver) {
      this.drawerObserver.disconnect();
    }
  }

  async updateFromCart() {
    try {
      const response = await fetch(`${routes.cart_url}.js`);
      const cart = await response.json();
      this.updateProgressBar(cart);
    } catch (error) {
      console.error('Error fetching cart:', error);
    }
  }

  updateProgressBar(cartData) {
    const currentTotal = cartData.total_price || 0;
    const maxThreshold = this.surpriseGiftThreshold;
    
    // Calculate progress percentage
    let progressPercentage;
    if (currentTotal >= this.surpriseGiftThreshold) {
      progressPercentage = 100;
    } else {
      progressPercentage = Math.min((currentTotal / maxThreshold) * 100, 100);
    }

    // Update progress bar fill
    const fillElement = this.querySelector('.cart-progress-bar__fill');
    if (fillElement) {
      fillElement.style.width = `${progressPercentage}%`;
    }

    // Update milestones
    const freeShippingMilestone = this.querySelector('.cart-progress-bar__milestone--free-shipping');
    const surpriseGiftMilestone = this.querySelector('.cart-progress-bar__milestone--surprise-gift');

    if (freeShippingMilestone) {
      if (currentTotal >= this.freeShippingThreshold) {
        freeShippingMilestone.classList.add('cart-progress-bar__milestone--achieved');
      } else {
        freeShippingMilestone.classList.remove('cart-progress-bar__milestone--achieved');
      }
    }

    if (surpriseGiftMilestone) {
      if (currentTotal >= this.surpriseGiftThreshold) {
        surpriseGiftMilestone.classList.add('cart-progress-bar__milestone--achieved');
      } else {
        surpriseGiftMilestone.classList.remove('cart-progress-bar__milestone--achieved');
      }
    }

    // Update message
    this.updateMessage(currentTotal);

    // Show/hide based on cart state
    const cartItems = this.closest('cart-items') || this.closest('cart-drawer-items');
    const cartDrawer = this.closest('cart-drawer');
    
    if (cartData.item_count === 0) {
      this.style.display = 'none';
    } else {
      this.style.display = '';
    }
  }

  updateMessage(currentTotal) {
    const messageElement = this.querySelector('.cart-progress-bar__message');
    if (!messageElement) return;

    const remainingForFreeShipping = Math.max(0, this.freeShippingThreshold - currentTotal);
    const remainingForSurpriseGift = Math.max(0, this.surpriseGiftThreshold - currentTotal);

    let messageHTML = '';

    if (currentTotal < this.freeShippingThreshold) {
      messageHTML = `
        <span class="cart-progress-bar__text">
          Add <strong>${this.formatMoney(remainingForFreeShipping)}</strong> more for <strong>free shipping</strong>
        </span>
      `;
      messageElement.classList.remove('cart-progress-bar__message--achieved');
    } else if (currentTotal < this.surpriseGiftThreshold) {
      messageHTML = `
        <span class="cart-progress-bar__text">
          🎉 <strong>Free shipping unlocked!</strong> Add <strong>${this.formatMoney(remainingForSurpriseGift)}</strong> more for a <strong>surprise gift</strong>
        </span>
      `;
      messageElement.classList.add('cart-progress-bar__message--achieved');
    } else {
      messageHTML = `
        <span class="cart-progress-bar__text">
          🎁 <strong>Congratulations! You've unlocked free shipping and a surprise gift!</strong>
        </span>
      `;
      messageElement.classList.add('cart-progress-bar__message--achieved');
    }

    messageElement.innerHTML = messageHTML;
  }

  formatMoney(cents) {
    // Format cents to money format (e.g., 5000 -> $50.00)
    // Try Shopify's formatMoney if available
    if (typeof window.Shopify !== 'undefined' && window.Shopify.formatMoney) {
      return window.Shopify.formatMoney(cents, window.money_format || '${{amount}}');
    }
    
    // Try to get money format from theme settings
    if (typeof window.money_format !== 'undefined') {
      return window.money_format.replace('{{amount}}', (cents / 100).toFixed(2));
    }
    
    // Fallback formatter
    const amount = (cents / 100).toFixed(2);
    return '$' + amount;
  }
}

customElements.define('cart-progress-bar', CartProgressBar);

